import React from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
} from "@mui/material";
import "./CheckModal.css";

function ConvertModal({
    open,
    onClose,
    analysisData,
    analyzeLoading,
    onConvert,
    convertLoading,
}) {
    const summary = analysisData?.summary;
    const ready = analysisData?.ready_for_convert || [];
    const modified = analysisData?.modified_files || [];
    const unknown = analysisData?.unknown_files || [];
    const restricted = analysisData?.restricted_bin_files || [];

    const convertDisabled =
        analyzeLoading ||
        !analysisData ||
        !analysisData?.files_ready ||
        analysisData.files_ready.length === 0;

    const getIdentifier = (f) => {
        if (f.bins?.length) return f.bins.join("\n");
        if (f.gov_codes?.length) return f.gov_codes.join("\n");
        return "-";
    };

    const renderEmptyRow = (colSpan, text = "Нет данных") => (
        <TableRow>
            <TableCell colSpan={colSpan} align="center" className="precheck-empty">
                {text}
            </TableCell>
        </TableRow>
    );

    return (
        <Modal
            open={open}
            onClose={analyzeLoading ? undefined : onClose}
            disableEscapeKeyDown={analyzeLoading}
        >
            <Box className="precheck-modal-shell">
                <div className="precheck-modal-topbar">
                    <div className="precheck-modal-badge">Предварительная проверка конвертации</div>
                    <Typography variant="h5" className="precheck-modal-title">
                        Результаты предварительного анализа
                    </Typography>
                </div>

                <Box className="precheck-scroll">
                    {analyzeLoading ? (
                        <Box className="precheck-loading-wrap">
                            <CircularProgress size={58} />
                            <Typography className="precheck-loading-text">
                                Выполняется анализ документов...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {summary && (
                                <section className="precheck-hero-card">
                                    <div className="precheck-hero-head">
                                        <div>
                                            <div className="precheck-kicker">Сводка</div>
                                            <h3 className="precheck-hero-title">
                                                Общая информация
                                            </h3>
                                        </div>
                                    </div>

                                    <div className="precheck-summary-grid">
                                        <div className="precheck-stat-card">
                                            <span className="precheck-stat-label">Банков</span>
                                            <strong>{summary.banks ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card">
                                            <span className="precheck-stat-label">Файлов</span>
                                            <strong>{summary.files ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card">
                                            <span className="precheck-stat-label">Оригинальные</span>
                                            <strong>{summary.original ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card precheck-stat-ready">
                                            <span className="precheck-stat-label">Подготовлены</span>
                                            <strong>{summary.ready ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card precheck-stat-restricted">
                                            <span className="precheck-stat-label">Ограничены</span>
                                            <strong>{summary.restricted ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card precheck-stat-modified">
                                            <span className="precheck-stat-label">Модифицированные</span>
                                            <strong>{summary.modified ?? 0}</strong>
                                        </div>

                                        <div className="precheck-stat-card precheck-stat-unknown">
                                            <span className="precheck-stat-label">Неизвестные</span>
                                            <strong>{summary.unknown ?? 0}</strong>
                                        </div>
                                    </div>
                                </section>
                            )}

                            <section className="precheck-section-card">
                                <div className="precheck-section-head">
                                    <div>
                                        <div className="precheck-kicker">Подготовка</div>
                                        <Typography variant="h6" className="precheck-section-title">
                                            Подготовлены к конвертации
                                        </Typography>
                                    </div>
                                </div>

                                <div className="precheck-table-wrap">
                                    <Table size="small" className="precheck-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>№</TableCell>
                                                <TableCell>Банк</TableCell>
                                                <TableCell>Файлов</TableCell>
                                                <TableCell>Платежей</TableCell>
                                                <TableCell>Страниц</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {ready.length > 0 ? (
                                                ready.map((b, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell>{b.bank_name || "-"}</TableCell>
                                                        <TableCell>{b.files ?? 0}</TableCell>
                                                        <TableCell>{b.payments ?? 0}</TableCell>
                                                        <TableCell>{b.pages ?? 0}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                renderEmptyRow(5)
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </section>

                            <section className="precheck-section-card">
                                <div className="precheck-section-head">
                                    <div>
                                        <div className="precheck-kicker">Исключения</div>
                                        <Typography variant="h6" className="precheck-section-title">
                                            Исключены из конвертации
                                        </Typography>
                                    </div>
                                </div>

                                <Typography variant="subtitle1" className="precheck-subtitle">
                                    Исключены по подписке
                                </Typography>

                                <div className="precheck-table-wrap">
                                    <Table size="small" className="precheck-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>№</TableCell>
                                                <TableCell>Файл</TableCell>
                                                <TableCell>Платежей</TableCell>
                                                <TableCell>Страниц</TableCell>
                                                <TableCell>Предполагаемый банк</TableCell>
                                                <TableCell>Идентификатор</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {restricted.length > 0 ? (
                                                restricted.map((f, i) => (
                                                    <TableRow key={i} className="precheck-row-restricted">
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell>{f.file || "-"}</TableCell>
                                                        <TableCell>{f.payments ?? 0}</TableCell>
                                                        <TableCell>{f.pages ?? 0}</TableCell>
                                                        <TableCell>{f.bank_name || "-"}</TableCell>
                                                        <TableCell className="precheck-bins">
                                                            {getIdentifier(f)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                renderEmptyRow(6)
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>

                                <Typography variant="subtitle1" className="precheck-subtitle">
                                    Модифицированные
                                </Typography>

                                <div className="precheck-table-wrap">
                                    <Table size="small" className="precheck-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>№</TableCell>
                                                <TableCell>Файл</TableCell>
                                                <TableCell>Платежей</TableCell>
                                                <TableCell>Страниц</TableCell>
                                                <TableCell>Предполагаемый банк</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {modified.length > 0 ? (
                                                modified.map((f, i) => (
                                                    <TableRow key={i} className="precheck-row-modified">
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell>{f.file || "-"}</TableCell>
                                                        <TableCell>{f.payments ?? 0}</TableCell>
                                                        <TableCell>{f.pages ?? 0}</TableCell>
                                                        <TableCell>{f.bank_name || "-"}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                renderEmptyRow(5)
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>

                                <Typography variant="subtitle1" className="precheck-subtitle">
                                    Неизвестного формата
                                </Typography>

                                <div className="precheck-table-wrap">
                                    <Table size="small" className="precheck-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>№</TableCell>
                                                <TableCell>Файл</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {unknown.length > 0 ? (
                                                unknown.map((f, i) => (
                                                    <TableRow key={i} className="precheck-row-unknown">
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell>{f.file || "-"}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                renderEmptyRow(2)
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </section>
                        </>
                    )}
                </Box>

                <Box className="precheck-sticky-footer">
                    <Button
                        variant="contained"
                        onClick={onConvert}
                        disabled={convertDisabled || convertLoading}
                        className="precheck-compare-btn"
                    >
                        {convertLoading ? "Конвертация..." : "Конвертировать"}
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={onClose}
                        className="precheck-close-btn"
                        disabled={analyzeLoading}
                    >
                        Закрыть
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

export default ConvertModal;